import { css } from '@emotion/react';
import { findKey, forEach, map } from 'lodash';
import nullthrows from 'nullthrows';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UIColorPalette } from './ColorPalette';

import { PlayableBots } from '@mythos/game/BotUtilities';
import { GameModules } from '@mythos/game/GameModules';
import { GameOptions, getGameOptionData } from '@mythos/game/GameOptions';
import { nonNull } from '@mythos/utils/utils';
import { match } from 'ts-pattern';
import { useShallow } from 'zustand/shallow';
import { useFragment } from './generated/gql';
import { UserSummaryFragmentDoc } from './GraphQLHelpers';
import LobbyButton from './LobbyButton';
import LobbyMutator from './LobbyMutator';
import { MEDIA_QUERY_MOBILE } from './mobile_ui/MStyles';
import { subscribeToTopic, unsubscribeFromTopic } from './stores/channelStore';
import { useLobbyStore } from './stores/lobbyStore';
import { useSessionStore } from './stores/sessionStore';
import UserHoverView from './UserHoverView';

function LobbyPage() {
  const session = useSessionStore((state) => state.session);
  const params = useParams();
  const lobbyID = params.lobbyID!;
  const { lobby, fetchLobby } = useLobbyStore(
    useShallow((state) => ({
      lobby: state.lobbyByID[lobbyID],
      fetchLobby: state.fetchLobby,
    })),
  );

  useEffect(() => {
    // XXX this might overfetch but it should prevent stale data
    fetchLobby(lobbyID, Infinity);
  }, [lobbyID, fetchLobby]);
  useEffect(() => {
    const subscription = subscribeToTopic(
      `lobby/${lobbyID}`,
      (message: any) => {
        useLobbyStore.getState().fetchLobby(lobbyID, message.sequenceID);
      },
    );
    return () => {
      unsubscribeFromTopic(subscription);
    };
  }, [lobbyID]);

  const navigate = useNavigate();
  useEffect(() => {
    if (lobby && lobby.game) {
      navigate('/game/' + lobby.game.id, { replace: true });
    }
  }, [lobby, navigate]);

  const onStartGame = useCallback(async () => {
    const mutator = new LobbyMutator(session, lobbyID);
    const gameID = await mutator.startGame();
    navigate('/game/' + gameID, { replace: true });
  }, [session, lobbyID, navigate]);

  const [botKey, setBotKey] = React.useState(Object.keys(PlayableBots)[0]);
  const onAddBot = useCallback(() => {
    var mutator = new LobbyMutator(session, lobbyID);
    mutator.addBot(botKey);
  }, [session, lobbyID, botKey]);

  const onLeave = useCallback(async () => {
    const mutator = new LobbyMutator(session, lobbyID);
    await mutator.leaveLobby();
    navigate('/');
  }, [session, lobbyID, navigate]);

  const onKick = useCallback(
    async (player_id: string) => {
      const mutator = new LobbyMutator(session, lobbyID);
      await mutator.kickPlayer(player_id);
    },
    [session, lobbyID],
  );

  const onJoin = useCallback(async () => {
    const mutator = new LobbyMutator(session, lobbyID);
    await mutator.joinGame();
  }, [session, lobbyID]);

  const setBotNameCB = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const botKey = findKey(PlayableBots, (bot) => {
      return bot.name === e.target.value;
    });
    if (botKey) {
      setBotKey(botKey);
    }
  };

  const setGameOption = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: keyof GameOptions,
  ) => {
    let mutator = new LobbyMutator(session, lobbyID);
    mutator.setGameOption(key, e.target.checked);
  };

  const setGameOptionSelect = (
    e: React.ChangeEvent<HTMLSelectElement>,
    key: keyof GameOptions,
  ) => {
    let mutator = new LobbyMutator(session, lobbyID);
    mutator.setGameOption(key, e.target.value as GameOptions[typeof key]);
  };

  const setModuleEnabled = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    let mutator = new LobbyMutator(session, lobbyID);
    mutator.setModule(key, e.target.checked);
  };

  const playerIDToBotType = useMemo(() => {
    if (!lobby) {
      return {};
    }
    return Object.fromEntries(
      lobby.playerIDToBotType.map(({ playerID, botType }) => [
        playerID,
        botType,
      ]),
    );
  }, [lobby]);

  if (lobby === null) {
    return <div>Lobby not found</div>;
  }
  if (lobby === undefined) {
    return <div>Loading lobby...</div>;
  }

  const host = useFragment(UserSummaryFragmentDoc, lobby.host);
  const isHost = session?.getUserID() === host.id;
  const players = lobby.players.edges
    ?.map((edge) =>
      edge?.node ? useFragment(UserSummaryFragmentDoc, edge.node) : null,
    )
    .filter(nonNull);
  const inLobby = players?.some((user) => user.id === session?.getUserID());

  const user_entries = players?.map((user) => {
    const profile_picture = (
      <img css={LobbyStyles.profilePicture} src={user.profilePictureURL} />
    );
    return (
      <li key={user.id} css={LobbyStyles.lobbyPlayerEntry}>
        <UserHoverView user={user}>
          <span css={LobbyStyles.lobbyPlayerEntryProfile}>
            {profile_picture}
            {user.name}
            {playerIDToBotType[user.id] &&
              ` (${
                PlayableBots[
                  playerIDToBotType[user.id] as keyof typeof PlayableBots
                ].name
              })`}
          </span>
        </UserHoverView>
        {isHost && user.id !== session?.getUserID() && (
          <LobbyButton
            css={LobbyStyles.lobbyControlButton}
            secondary={true}
            onClick={() => onKick(user.id)}
          >
            Kick
          </LobbyButton>
        )}
      </li>
    );
  });
  let leave_button = inLobby && (
    <LobbyButton
      css={LobbyStyles.lobbyControlButton}
      secondary={true}
      onClick={onLeave}
    >
      Leave
    </LobbyButton>
  );
  let join_button = !inLobby && (
    <LobbyButton
      css={LobbyStyles.lobbyControlButton}
      secondary={true}
      onClick={onJoin}
    >
      Join
    </LobbyButton>
  );
  let controls = <></>;
  let game_options = JSON.parse(lobby.options);
  let modules = lobby.modules;
  let option_data = getGameOptionData();

  forEach(option_data, (option, key) => {
    if (
      option.displayMode === 'hidden' ||
      (option.displayMode === 'devOnly' && !session?.getUser().isDev)
    ) {
      delete option_data[key as keyof typeof option_data];
    }
  });

  if (isHost) {
    let start_button = (
      <LobbyButton
        key="start"
        css={LobbyStyles.lobbyControlButton}
        onClick={onStartGame}
      >
        Start Game
      </LobbyButton>
    );
    let add_bot_button = (
      <LobbyButton
        key="bot"
        secondary={true}
        css={LobbyStyles.lobbyControlButton}
        onClick={onAddBot}
      >
        Add Bot
      </LobbyButton>
    );
    let bot_options = map(PlayableBots, (bot) => {
      if (
        bot.displayMode === 'visible' ||
        (bot.displayMode === 'devOnly' && session?.getUser().isDev)
      ) {
        return (
          <option key={bot.name} value={bot.name}>
            {bot.name}
          </option>
        );
      }
    });
    let add_bot_selector = (
      <select
        name="botselect"
        id="botselect"
        key="botselector"
        css={LobbyStyles.selector}
        defaultValue={
          nullthrows(PlayableBots[botKey as keyof typeof PlayableBots]).name
        }
        onChange={setBotNameCB}
      >
        {bot_options}
      </select>
    );

    let game_options_view = map(option_data, (option, key) => {
      return (
        <div css={LobbyStyles.lobbyGameOption} key={key}>
          <span
            className="lobby-game-option-tooltip"
            css={LobbyStyles.lobbyGameOptionTooltip}
          >
            {option.tooltip}
          </span>
          {option.name}:
          {match(option)
            .with({ type: 'boolean' }, (option) => {
              return (
                <input
                  css={LobbyStyles.lobbyGameOptionCheckbox}
                  type="checkbox"
                  checked={game_options[key]}
                  onChange={(e) => setGameOption(e, option.key)}
                />
              );
            })
            .with({ type: 'enum' }, (option) => {
              return (
                <select
                  css={LobbyStyles.lobbyGameOptionSelect}
                  value={game_options[key]}
                  onChange={(e) => setGameOptionSelect(e, option.key)}
                >
                  {Object.keys(option.valueToDisplayName).map((value) => (
                    <option key={value} value={value}>
                      {option.valueToDisplayName[value]}
                    </option>
                  ))}
                </select>
              );
            })
            .exhaustive()}
        </div>
      );
    });
    const modules_view = GameModules.map((module) => {
      if (module.state === 'disabled') {
        return null;
      }
      if (module.state === 'devOnly' && !session?.getUser().isDev) {
        return null;
      }
      return (
        <div css={LobbyStyles.lobbyGameOption} key={module.key}>
          <span
            className="lobby-game-option-tooltip"
            css={LobbyStyles.lobbyGameOptionTooltip}
          >
            {module.description}
          </span>
          {module.name}:
          <input
            css={LobbyStyles.lobbyGameOptionCheckbox}
            type="checkbox"
            checked={modules.includes(module.key)}
            onChange={(e) => setModuleEnabled(e, module.key)}
          />
        </div>
      );
    }).filter(nonNull);
    controls = (
      <>
        <div css={LobbyStyles.lobbyControls}>
          {leave_button}
          {join_button}
          {start_button}
        </div>
        <ul css={LobbyStyles.lobbySettingsTable}>
          <li css={LobbyStyles.lobbySettingsRow}>
            <span css={LobbyStyles.lobbySettingsCellText}>Bots:</span>
            <span css={LobbyStyles.lobbySettingsCellSelect}>
              {add_bot_selector}
            </span>
            <span css={LobbyStyles.lobbySettingsCellButton}>
              {add_bot_button}
            </span>
          </li>
          {modules_view.length > 0 && (
            <>
              <li css={LobbyStyles.lobbyGameOptionText}>Modules:</li>
              <li css={LobbyStyles.lobbyGameOptionText}>{modules_view}</li>
            </>
          )}
          <li css={LobbyStyles.lobbyGameOptionText}>Game Options:</li>
          <li css={LobbyStyles.lobbyGameOptionText}>{game_options_view}</li>
        </ul>
      </>
    );
  } else {
    let game_options_view = map(option_data, (option, key) => {
      return (
        <div css={LobbyStyles.lobbyGameOption} key={key}>
          <span css={LobbyStyles.lobbyGameOptionTooltip}>{option.tooltip}</span>
          {option.name}:{' '}
          <span css={LobbyStyles.lobbyGameOptionValue}>
            {match(option)
              .with({ type: 'boolean' }, (option) => {
                return game_options[key] ? 'yes' : 'no';
              })
              .with({ type: 'enum' }, (option) => {
                return option.valueToDisplayName[game_options[key]];
              })
              .exhaustive()}
          </span>
        </div>
      );
    });
    const modules_view = lobby.modules
      .map((module) => {
        const moduleDef = GameModules.find((x) => x.key === module);
        if (!moduleDef) {
          return null;
        }
        return (
          <div css={LobbyStyles.lobbyGameOption}>
            <span css={LobbyStyles.lobbyGameOptionTooltip}>
              {moduleDef.description}
            </span>
            <span css={LobbyStyles.lobbyGameOptionValue}>{moduleDef.name}</span>
          </div>
        );
      })
      .filter(nonNull);
    controls = (
      <>
        <div css={LobbyStyles.lobbyControls}>
          {leave_button}
          {join_button}
        </div>
        <ul css={LobbyStyles.lobbySettingsTable}>
          {modules_view.length > 0 && (
            <>
              <li css={LobbyStyles.lobbyGameOptionText}>Modules:</li>
              <li css={LobbyStyles.lobbyGameOptionText}>{modules_view}</li>
            </>
          )}
          <li css={LobbyStyles.lobbyGameOptionText}>Game Options:</li>
          <li css={LobbyStyles.lobbyGameOptionText}>{game_options_view}</li>
        </ul>
      </>
    );
  }
  return (
    <div css={LobbyStyles.lobbyPageContainer}>
      <div css={LobbyStyles.lobbyContainer}>
        <span css={LobbyStyles.lobbyHeader}>{lobby.name}</span>
        <ul css={LobbyStyles.lobbyPlayerList}>{user_entries}</ul>
        <div>{controls}</div>
      </div>
    </div>
  );
}

const LobbyStyles = {
  lobbyPageContainer: css({
    padding: '0px 20px 0px 20px',
  }),
  lobbyContainer: css({
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    marginTop: '30px',
    maxWidth: '820px',

    [MEDIA_QUERY_MOBILE]: {
      marginTop: 5,
    },
  }),
  lobbyHeader: css({
    padding: '14px',
    textAlign: 'center',
    color: UIColorPalette.lobbyDarkText,
    backgroundColor: UIColorPalette.lobbyDarkerBG,
    borderBottom: `1px solid ${UIColorPalette.lobbyDarkBorder}`,
  }),
  lobbyPlayerList: css({
    height: '230px',
    backgroundColor: UIColorPalette.lobbyPlayerListBg,
    width: '100%',
    paddingBottom: '3px',
  }),
  lobbyPlayerEntry: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${UIColorPalette.lobbyDarkBorderBottom}`,
    backgroundColor: UIColorPalette.lobbyDarkBG,
    height: '20%',
  }),
  lobbyPlayerEntryProfile: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '40%',
    paddingLeft: '3px',
  }),
  lobbyPlayerEntryTime: css({
    width: '50%',
  }),
  lobbyPlayerEntryWins: css({
    width: '10%',
  }),
  lobbyControls: css({
    display: 'flex',
    flexDirection: 'row',
    flexShrink: '0',
    justifyContent: 'space-between',
    padding: '10px',
    backgroundColor: UIColorPalette.lobbyDarkerBG,
  }),
  lobbySettingsTable: css({
    width: '100%',
    backgroundColor: UIColorPalette.lobbyDarkerBG,
    paddingTop: '6px',
    paddingBottom: '10px',
  }),
  lobbySettingsRow: css({
    padding: '5px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  }),
  lobbySettingsCellText: css({
    display: 'flex',
    flexDirection: 'row',
    color: UIColorPalette.buttonPrimary,
    width: '10%',
    paddingLeft: '15px',
  }),
  lobbySettingsCellSelect: css({
    flex: '1 0 3',
  }),
  lobbySettingsCellButton: css({
    flex: '1 1 3',
  }),
  lobbyControlButton: css({
    minWidth: '100px',
    height: '30px',
    margin: '5px',
    marginLeft: '10px',
    marginRight: '10px',

    borderRadius: '2px',
    outline: 'none',

    [MEDIA_QUERY_MOBILE]: {
      minWidth: 0,
      paddingLeft: 20,
      paddingRight: 20,
    },
  }),
  profilePicture: css({
    height: '32px',
    width: '32px',
    padding: '4px',
    paddingRight: '6px',
  }),
  lobbyGameOptionText: css({
    color: UIColorPalette.buttonPrimary,
    padding: '5px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '10px',
    alignItems: 'center',
    marginLeft: '15px',
    marginTop: '10px',
  }),
  lobbyGameOption: css({
    display: 'flex',
    flexDirection: 'row',
    minWidth: '15%',
    marginRight: '15px',
    '&:hover .lobby-game-option-tooltip': {
      visibility: 'visible',
    },
  }),
  lobbyGameOptionTooltip: css({
    visibility: 'hidden',
    maxWidth: '250px',
    backgroundColor: UIColorPalette.lobbyTooltipBg,
    color: UIColorPalette.lobbyDarkText,
    textAlign: 'left',
    borderRadius: '2px',
    marginTop: '25px',
    padding: '5px 5px',
    position: 'absolute',
    zIndex: '1',
  }),
  lobbyGameOptionCheckbox: css({
    marginLeft: '5px',
    marginTop: '0px',
  }),
  lobbyGameOptionSelect: css({
    marginLeft: '5px',
    marginTop: '0px',
  }),
  lobbyGameOptionValue: css({
    color: UIColorPalette.lobbyDarkText,
    marginLeft: '5px',
    minWidth: '30px',
  }),
  selector: css({
    display: 'inline-block',
    minWidth: '160px',

    padding: '4.5px',

    paddingLeft: '5px',
    paddingRight: '5px',

    marginLeft: '10px',
    marginRight: '10px',

    borderRadius: '2px',
    border: 'none',
    outline: 'none',

    color: UIColorPalette.lobbyDarkText,
    backgroundColor: UIColorPalette.lobbyTooltipBg,

    [MEDIA_QUERY_MOBILE]: {
      minWidth: 0,
    },
  }),
};

export default LobbyPage;
